import React from "react";
import { X } from "lucide-react";
import { cn } from "../lib/utils";

interface BadgeProps {
  icon?: React.ReactNode;
  text?: string;
  className?: string;
  variant: "default" | "dark" | "success" | "warning" | "error";
  "data-testid"?: string;
  /** Renders a trailing dismiss button. Omit for a non-removable badge. */
  onRemove?: () => void;
  /** Accessible name for the dismiss button — required whenever onRemove is set,
   *  since the button has no visible text of its own. */
  removeLabel?: string;
  removeTestId?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  icon,
  text,
  className,
  variant,
  "data-testid": dataTestId,
  onRemove,
  removeLabel,
  removeTestId,
}) => {
  const variantClassName =
    variant === "dark"
      ? // Matches the black pills the search bar and primary buttons already use.
        "bg-primary text-on-primary"
      : variant === "success"
        ? "bg-status-success text-on-status"
        : variant === "warning"
          ? "bg-status-warning text-on-status"
          : variant === "error"
            ? "bg-status-error text-on-status"
            : "bg-accent-soft text-ink";

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-badge px-2 py-1.5",
        variantClassName,
        className,
      )}
    >
      {icon && (
        <span className="flex h-3.5 w-3.5 items-center justify-center">
          {icon}
        </span>
      )}
      {text && (
        <span className="text-xs font-medium leading-[1.333]">{text}</span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel}
          data-testid={removeTestId}
          // -mr-0.5 pulls the icon back to the pill's optical edge, which the
          // wider glyph box would otherwise push inward.
          className="-mr-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full opacity-70 transition-opacity hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};
